import type { Unit } from './countdown';

const UNIT_LABELS: Record<Unit, { singular: string; plural: string }> = {
  Year:   { singular: 'YEAR',   plural: 'YEARS'  },
  Month:  { singular: 'MONTH',  plural: 'MONTHS' },
  Week:   { singular: 'WEEK',   plural: 'WEEKS'  },
  Day:    { singular: 'DAY',    plural: 'DAYS'   },
  Hour:   { singular: 'HOUR',   plural: 'HOURS'  },
  Minute: { singular: 'MIN',    plural: 'MIN'    },
  Second: { singular: 'SEC',    plural: 'SEC'    },
};

/// Plural/singular picker matching countdown.hbs:unitLabel — value === 1 gets
/// the singular form, anything else gets plural. Min/sec abbreviations don't
/// inflect, so both forms are identical for those. The asterisk on Day marks
/// a weekdays-only count, as the iOS app does.
export function unitLabel(unit: Unit, value: number, countOnlyWeekdays = false): string {
  const label = value === 1 ? UNIT_LABELS[unit].singular : UNIT_LABELS[unit].plural;
  return unit === 'Day' && countOnlyWeekdays ? `${label}*` : label;
}

const COMPACT_UNIT_WORDS: Record<Unit, { singular: string; plural: string }> = {
  Year:   { singular: 'year',   plural: 'years'   },
  Month:  { singular: 'month',  plural: 'months'  },
  Week:   { singular: 'week',   plural: 'weeks'   },
  Day:    { singular: 'day',    plural: 'days'    },
  Hour:   { singular: 'hour',   plural: 'hours'   },
  Minute: { singular: 'minute', plural: 'minutes' },
  Second: { singular: 'second', plural: 'seconds' },
};

/// "days left" / "day ago" / "Today" — mirrors iOS's
/// DateFormattingUtils.getStringsForValueAndUnit. `number` may be negative for
/// past events; absolute value is what we render visually (the sign carries
/// "ago" vs "left"). Special-cases value=0 as "Today" because the cascade in
/// compactTimeLeftFrom returns 0/Second when the event is today.
export function compactCountdownLabel(value: number, unit: Unit, countOnlyWeekdays = false): string {
  if (value === 0) return 'Today';
  const abs = Math.abs(value);
  const isPlural = abs !== 1;
  const word = isPlural ? COMPACT_UNIT_WORDS[unit].plural : COMPACT_UNIT_WORDS[unit].singular;
  const label = value < 0 ? `${word} ago` : `${word} left`;
  return unit === 'Day' && countOnlyWeekdays ? `${label}*` : label;
}

export function formatDueDate(d: Date, allDay: boolean, locale: string = navigator.language): string {
  const opts: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };
  if (!allDay) {
    opts.hour = 'numeric';
    opts.minute = '2-digit';
  }
  try {
    return d.toLocaleString(locale, opts);
  } catch {
    return d.toDateString();
  }
}

/// Short list-cell date format matching iOS's
/// DateFormattingUtils.shortDateFormat ("E d MMM yyyy"), with the clock
/// appended for a timed countdown — same rule the iOS list cell applies via
/// the withHoursAndMinutes flag.
export function formatShortDueDate(d: Date, allDay: boolean, locale: string = navigator.language): string {
  const dateOpts: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  };
  try {
    const dateStr = d.toLocaleString(locale, dateOpts);
    if (allDay) return dateStr;
    const time = d.toLocaleString(locale, { hour: '2-digit', minute: '2-digit', hour12: false });
    return `${dateStr} ${time}`;
  } catch {
    return d.toDateString();
  }
}

/// Format a Date for an <input type="datetime-local"> value (YYYY-MM-DDTHH:mm).
export function dateToLocalISO(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/// Parse the value coming out of <input type="datetime-local"> as local time.
export function localISOToDate(iso: string): Date {
  return new Date(iso);
}
